import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { hardReload } from './hardReload'

/**
 * Экран, который грузится отдельным чанком при первом переходе (fb-04: бандл
 * одним куском весил 2 МБ, и на старт тянулись графики, тренировки и кураторские
 * экраны, которых клиент в этот момент не видит).
 *
 * Чанки опасны устаревшим бандлом: Telegram не перезагружает свёрнутый Mini App,
 * человек неделями сидит на старой сборке, а после деплоя её чанков на сервере
 * уже нет. Если догрузка упала и на сервере другая сборка — перезагружаемся на
 * неё (один раз за жизнь документа). Если сборка та же — это сеть, отдаём
 * ошибку как есть.
 */

type Loader = () => Promise<{ default: ComponentType<any> }>

export type LazyPage = LazyExoticComponent<ComponentType<any>> & { preload: () => void }

const CURRENT_BUILD_ID = (import.meta.env.VITE_BUILD_ID as string | undefined) ?? null

let reloadStarted = false

async function serverBuildId(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' })
    if (!res.ok) return null
    const data = (await res.json()) as { buildId?: unknown }
    return typeof data.buildId === 'string' && data.buildId ? data.buildId : null
  } catch {
    return null
  }
}

/** Решение по упавшей догрузке чанка — чистое, для тестов. */
export function shouldReloadForChunkError(input: {
  current: string | null
  server: string | null
  alreadyReloading: boolean
}): boolean {
  const { current, server, alreadyReloading } = input
  return !alreadyReloading && !!current && !!server && current !== server
}

async function recoverFromChunkError(err: unknown): Promise<never> {
  const server = await serverBuildId()
  if (shouldReloadForChunkError({ current: CURRENT_BUILD_ID, server, alreadyReloading: reloadStarted })) {
    reloadStarted = true
    hardReload(server as string)
    // Документ сейчас выгрузится — Suspense держит заглушку до перехода.
    return new Promise<never>(() => {})
  }
  throw err
}

export function lazyPage<M>(load: () => Promise<M>, pick: (m: M) => ComponentType<any>): LazyPage {
  let pending: ReturnType<Loader> | null = null
  const fetchModule: Loader = () => {
    pending ??= load().then(
      (m) => ({ default: pick(m) }),
      (err) => {
        pending = null // следующая попытка — заново, а не закэшированный отказ
        throw err
      },
    )
    return pending
  }
  // Перезагрузка — только когда человек сам перешёл на экран. Фоновая догрузка
  // молча глотает ошибку: иначе она перезагрузила бы приложение посреди ввода.
  const Page = lazy(() => fetchModule().catch(recoverFromChunkError)) as LazyPage
  Page.preload = () => { fetchModule().catch(() => { /* догрузим при переходе */ }) }
  return Page
}
